import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export enum ToastType {
  ERROR = 'error',
  WARNING = 'warning',
  SUCCESS = 'success',
  INFO = 'info'
}

export interface Toast {
  id: string;
  message: string;
  title: string;
  type: ToastType;
  autoClose?: boolean;
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toasts: BehaviorSubject<Toast[]> = new BehaviorSubject<Toast[]>([]);
  public toasts$: Observable<Toast[]> = this.toasts.asObservable();
  
  constructor() {}

  /**
   * Show a new toast notification
   * @param title The title of the toast
   * @param message The message content of the toast
   * @param type The type of toast (error, warning, success, info)
   * @param autoClose Whether the toast should close automatically
   * @param duration Duration in milliseconds before auto-closing (default: 5000)
   * @returns The ID of the created toast
   */
  show(
    title: string,
    message: string,
    type: ToastType = ToastType.INFO,
    autoClose: boolean = true,
    duration: number = 5000
  ): string {
    const id = this.generateId();
    
    const toast: Toast = {
      id,
      title,
      message,
      type,
      autoClose,
      duration
    };
    
    const currentToasts = this.toasts.getValue();
    this.toasts.next([...currentToasts, toast]);
    
    if (autoClose) {
      setTimeout(() => this.remove(id), duration);
    }
    
    return id;
  }

  /**
   * Show an error toast
   * @param title The title of the toast
   * @param message The message content of the toast
   * @param autoClose Whether the toast should close automatically
   * @param duration Duration in milliseconds before auto-closing
   * @returns The ID of the created toast
   */
  error(title: string, message: string, autoClose: boolean = true, duration: number = 5000): string {
    return this.show(title, message, ToastType.ERROR, autoClose, duration);
  }

  /**
   * Show a warning toast
   * @param title The title of the toast
   * @param message The message content of the toast
   * @param autoClose Whether the toast should close automatically
   * @param duration Duration in milliseconds before auto-closing
   * @returns The ID of the created toast
   */
  warning(title: string, message: string, autoClose: boolean = true, duration: number = 5000): string {
    return this.show(title, message, ToastType.WARNING, autoClose, duration);
  }

  /**
   * Show a success toast
   * @param title The title of the toast
   * @param message The message content of the toast
   * @param autoClose Whether the toast should close automatically
   * @param duration Duration in milliseconds before auto-closing
   * @returns The ID of the created toast
   */
  success(title: string, message: string, autoClose: boolean = true, duration: number = 5000): string {
    return this.show(title, message, ToastType.SUCCESS, autoClose, duration);
  }

  /**
   * Show an info toast
   * @param title The title of the toast
   * @param message The message content of the toast
   * @param autoClose Whether the toast should close automatically
   * @param duration Duration in milliseconds before auto-closing
   * @returns The ID of the created toast
   */
  info(title: string, message: string, autoClose: boolean = true, duration: number = 5000): string {
    return this.show(title, message, ToastType.INFO, autoClose, duration);
  }

  /**
   * Remove a toast by ID
   * @param id The ID of the toast to remove
   */
  remove(id: string): void {
    const currentToasts = this.toasts.getValue();
    const updatedToasts = currentToasts.filter(toast => toast.id !== id);
    this.toasts.next(updatedToasts);
  }

  /**
   * Clear all toasts
   */
  clear(): void {
    this.toasts.next([]);
  }

  /**
   * Generate a unique ID for a toast
   * @returns A unique string ID
   */
  private generateId(): string {
    return `toast-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`;
  }
}
